import { useState, useEffect } from 'react';

import { BsQuestionCircle } from 'react-icons/bs';

import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';

import AgenticSearchChat from '../../components/AgenticSearchChat/AgenticSearchChat';
import { LeadCriteriaModal } from '../../components/LeadOverviewPage/LeadCriteriaModal/LeadCriteriaModal';
import LeadOverviewContainer from '../../components/LeadOverviewPage/LeadOverviewContainer/LeadOverviewContainer';
import LeadSearchOverlay from '../../components/LeadSearchOverlay/LeadSearchOverlay';
import { useLeadData } from '../../contexts/LeadDataContext';
import { LeadOverviewProvider } from '../../contexts/LeadOverviewContext';
import { useLeadOverviewTour } from '../../hooks/useLeadOverviewTour';
import { useSearchFilters } from '../../hooks/useSearchFilters';
import { useSearchPreview } from '../../hooks/useSearchPreview';

const LeadOverviewPageInner = () => {
	const { handleSearchQuerySelect, searchQueries, fetchingQueries, queriesFetched } = useLeadData();

	const [isCriteriaOpen, setIsCriteriaOpen] = useState(false);
	const [initialQueriesSettled, setInitialQueriesSettled] = useState(false);

	// Paused until the first settled query result so the auto-started tour cannot
	// race the auto-opened criteria modal (on slow connections the queries fetch
	// outlasts the tour's start delay), and while the modal is open so the tour
	// never highlights elements underneath its overlay.
	const { startTour } = useLeadOverviewTour({ paused: !initialQueriesSettled || isCriteriaOpen });

	const {
		filters,
		searchType,
		isGermany,
		hasActiveHRFilters,
		hasActiveMapsFilters,
		handleFilterChange,
		handleStartSearch,
		handleSearchTypeToggle,
	} = useSearchFilters({ handleSearchQuerySelect });

	// Live, free Handelsregister match-count preview ("Treffer-Trichter").
	const {
		data: previewData,
		isLoading: previewIsLoading,
		isResolvingIndustry: previewIsResolving,
	} = useSearchPreview({
		filters,
		searchType,
		isGermany,
		enabled: isCriteriaOpen,
	});

	// Auto-open the popup once when the user genuinely has no prospect queries, so
	// the empty state guides them into their first search.
	//
	// The one-shot fires only on a *settled* result — the query has been fetched
	// and nothing is currently in flight. That's what makes an empty list
	// trustworthy: a transient empty state (initial load, or the refetch triggered
	// when arriving from onboarding where a search already exists) still has
	// isFetching=true and is skipped until the real result lands.
	useEffect(() => {
		if (initialQueriesSettled) return;
		if (!queriesFetched || fetchingQueries) return;

		setInitialQueriesSettled(true);
		if (searchQueries.length === 0) {
			setIsCriteriaOpen(true);
		}
	}, [initialQueriesSettled, queriesFetched, fetchingQueries, searchQueries.length]);

	const handleStartSearchFromModal = async () => {
		await handleStartSearch();
		setIsCriteriaOpen(false);
	};

	return (
		<>
			{/* content mode: the lead table shrinks to the remaining space and scrolls
			    internally, keeping its horizontal scrollbar always visible. */}
			<Page
				width="full"
				scrollMode="content"
				className="bg-gray-50"
				header={
					<PageHeader breadcrumbs={['Lead-Übersicht']}>
						<LeadSearchOverlay />
						<button
							onClick={startTour}
							className="p-2 text-bgBase-800 hover:text-primary-600 hover:bg-purple-50 rounded-lg transition-colors"
							title="Tour starten"
						>
							<BsQuestionCircle className="w-5 h-5" />
						</button>
					</PageHeader>
				}
			>
				<LeadOverviewContainer onNewSearch={() => setIsCriteriaOpen(true)} />
			</Page>

			<LeadCriteriaModal
				open={isCriteriaOpen}
				onClose={() => setIsCriteriaOpen(false)}
				filters={filters}
				searchType={searchType}
				isGermany={isGermany}
				hasActiveHRFilters={hasActiveHRFilters}
				hasActiveMapsFilters={hasActiveMapsFilters}
				onFilterChange={handleFilterChange}
				onStartSearch={handleStartSearchFromModal}
				onSearchTypeToggle={handleSearchTypeToggle}
				previewIsLoading={previewIsLoading}
				previewIsResolving={previewIsResolving}
				previewData={previewData}
			/>

			<AgenticSearchChat />
		</>
	);
};

const LeadOverviewPage = () => {
	return (
		<LeadOverviewProvider>
			<LeadOverviewPageInner />
		</LeadOverviewProvider>
	);
};

export default LeadOverviewPage;
